// Entering a draft held in person (BACKLOG D7, scenario C5, DRAFT_MODES §3.3):
// the commissioner's half of a real league's draft page (LeagueDraftScreen).
//
// TWO STEPS ON ONE PAGE. First the order the league drafted in: every team,
// arranged with up and down, starting from the order the page already shows.
// Then the picks, one at a time: whose pick it is, a search over the league's
// own pool with the players already taken left out, a tap to enter, the last
// one taken back, and Finish once every pick is in. Finish is said plainly
// before it is tapped — it sets every roster and tells every manager, and
// nothing on this page takes it back (CLAUDE.md, "Say what will happen").
//
// FAST IS THE POINT (C5: "entering 150 picks takes minutes, not an hour"). The
// search clears after each pick and the top of what is left shows before
// anything is typed, since a draft runs roughly in the order a ranking does;
// each answer is the draft as it now stands, drawn at once rather than after a
// second read, so the next tap is never a pick behind.
//
// Drawn through the kit (LOOKS.md Part 7).

import { useMemo, useState } from 'react';
import type { DraftPoolPlayer, DraftView, ManualDraftRequest } from '@fsp/domain';
import { draftOrderWords, valueOf } from '@fsp/domain';
import { messageOf } from '../data/api';
import { enterDraft, useDraftPool } from '../data/draftPlan';
import { PositionPill, TeamMark } from '../kit';
import {
  Button,
  Callout,
  Caption,
  Card,
  Choice,
  IconButton,
  ListRow,
  NumberField,
  RowName,
  RowSub,
  Stack,
  Text,
  TextField,
} from '../kit/ui';
import { TeamName } from './shared/Names';
import { clubLabel } from '../sports/registry';

/** How many of the pool to show at once. A search narrows the rest. */
const SHOWN = 8;

/** Lower case, without accents, so "Pena" finds "Peña". */
const plain = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * One step against the server, and the draft it answers with — or what went
 * wrong, in the server's words.
 */
function useStep(leagueId: string, onView: (view: DraftView) => void) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const step = async (body: ManualDraftRequest): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    const reply = await enterDraft(leagueId, body);
    setBusy(false);
    if (reply.status === 'ok') {
      onView(reply.data);
      return true;
    }
    setProblem(
      messageOf(reply, 'That did not go in.', 'Could not reach the site. Nothing was entered.'),
    );
    return false;
  };
  return { busy, problem, step };
}

/** The order the league drafted in, round one, before any pick is entered. */
export function DraftOrderEntry({
  view,
  onView,
  onDone,
}: {
  view: DraftView;
  onView: (view: DraftView) => void;
  /** Called once the order is saved, when this is a change rather than the first time. */
  onDone?: () => void;
}) {
  const teams = useMemo(() => new Map(view.seats.map((s) => [s.team.id, s.team])), [view.seats]);
  const [order, setOrder] = useState(() =>
    [...view.seats].sort((a, b) => a.slot - b.slot).map((s) => s.team.id),
  );
  const { busy, problem, step } = useStep(view.league.id, onView);
  const move = (from: number, to: number) =>
    setOrder((o) => {
      const next = [...o];
      [next[from], next[to]] = [next[to]!, next[from]!];
      return next;
    });

  return (
    <Stack gap={2}>
      <Caption>The order you drafted in, round one</Caption>
      <Card>
        {order.map((id, i) => {
          const team = teams.get(id);
          if (!team) return null;
          return (
            <ListRow
              key={id}
              leadSize="lg"
              lead={
                <span className="fsp-league-draft-slot">
                  <Text weight="bold" numeric>
                    {i + 1}
                  </Text>
                  <TeamMark team={team} size={22} />
                </span>
              }
              trail={
                <span className="fsp-draft-entry-moves">
                  <IconButton
                    icon="up"
                    label={`Move ${team.name} up`}
                    variant="quiet"
                    size="sm"
                    disabled={i === 0 || busy}
                    onClick={() => move(i, i - 1)}
                  />
                  <IconButton
                    icon="down"
                    label={`Move ${team.name} down`}
                    variant="quiet"
                    size="sm"
                    disabled={i === order.length - 1 || busy}
                    onClick={() => move(i, i + 1)}
                  />
                </span>
              }
            >
              <RowName>
                <TeamName team={team} />
              </RowName>
              <RowSub>{team.ownerName}</RowSub>
            </ListRow>
          );
        })}
      </Card>
      <Text tone="secondary" size="xs" block>
        {view.type === 'linear' || view.type === 'auction'
          ? 'Every round runs in this order.'
          : `${draftOrderWords(view.type).sentence} Set round one and the rest follows.`}
      </Text>
      {problem === null ? null : <Callout tone="warn">{problem}</Callout>}
      <Button
        variant="primary"
        icon="draft"
        loading={busy}
        onClick={() => {
          void step({ action: 'start', order }).then((done) => {
            if (done) onDone?.();
          });
        }}
      >
        {onDone ? 'Save the order' : 'Start entering picks'}
      </Button>
    </Stack>
  );
}

/** The picks, one at a time, then Finish. */
export function DraftPickEntry({
  view,
  onView,
}: {
  view: DraftView;
  onView: (view: DraftView) => void;
}) {
  const leagueId = view.league.id;
  const pool = valueOf(useDraftPool(leagueId).snapshot);
  const [query, setQuery] = useState('');
  const [reordering, setReordering] = useState(false);
  const { busy, problem, step } = useStep(leagueId, onView);

  const taken = useMemo(
    () => new Set(view.board.flatMap((c) => (c.player ? [c.player.id] : []))),
    [view.board],
  );
  const matches = useMemo((): DraftPoolPlayer[] => {
    if (pool === null) return [];
    const words = plain(query).split(/\s+/).filter(Boolean);
    return pool.players
      .filter((p) => !taken.has(p.playerId))
      .filter((p) => {
        const name = plain(p.name);
        return words.every((w) => name.includes(w));
      })
      .slice(0, SHOWN);
  }, [pool, query, taken]);

  const current = view.current;
  const team = current === null ? null : view.seats.find((s) => s.team.id === current.teamId);
  const last = view.lastPick;
  const teamCount = view.seats.length;
  const label = (overall: number) => {
    const round = Math.ceil(overall / teamCount);
    const slot = ((overall - 1) % teamCount) + 1;
    return `${round}.${String(slot).padStart(2, '0')}`;
  };

  if (reordering) {
    return <DraftOrderEntry view={view} onView={onView} onDone={() => setReordering(false)} />;
  }

  const undo =
    last === null ? null : (
      <Button
        variant="secondary"
        size="xs"
        icon="undo"
        disabled={busy}
        onClick={() => void step({ action: 'undo', overall: last.overall })}
      >
        {`Take back ${label(last.overall)}${last.player ? `, ${last.player.shortName}` : ''}`}
      </Button>
    );

  if (current === null) {
    return (
      <Stack gap={2}>
        <Callout tone="good">Every pick is in.</Callout>
        <Text size="sm" block>
          Finishing sets every team’s roster from these picks, starts each team’s lineup, puts a bot
          in any seat nobody has taken, and tells every manager. Nothing on this page takes it back.
        </Text>
        {problem === null ? null : <Callout tone="warn">{problem}</Callout>}
        <Button
          variant="primary"
          icon="accept"
          loading={busy}
          onClick={() => void step({ action: 'finish' })}
        >
          Finish the draft
        </Button>
        {undo}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Caption>{`Pick ${label(current.overall)} of ${label(view.board.length)}`}</Caption>
      {team ? (
        <ListRow leadSize="lg" lead={<TeamMark team={team.team} size={26} />} selected>
          <RowName>
            <TeamName team={team.team} />
          </RowName>
          <RowSub>{`is on the clock · round ${String(current.round)}`}</RowSub>
        </ListRow>
      ) : null}
      <TextField
        label="Find the player"
        placeholder="Type a name"
        icon="search"
        value={query}
        onChange={setQuery}
        autoFocus
      />
      {problem === null ? null : <Callout tone="warn">{problem}</Callout>}
      {pool === null ? (
        <Text tone="secondary" size="sm">
          Loading the players…
        </Text>
      ) : matches.length === 0 ? (
        <Text tone="secondary" size="sm">
          Nobody left by that name.
        </Text>
      ) : (
        <Card>
          {matches.map((p) => (
            <ListRow
              key={p.playerId}
              onClick={
                busy
                  ? undefined
                  : () => {
                      void step({
                        action: 'pick',
                        overall: current.overall,
                        playerId: p.playerId,
                      }).then((done) => {
                        if (done) setQuery('');
                      });
                    }
              }
              lead={<PositionPill pos={p.positions[0] ?? '—'} size="sm" />}
            >
              <RowName>{p.name}</RowName>
              <RowSub>{`${p.proTeam === null ? 'Free agent' : clubLabel(p.proTeam)} · #${String(p.rank)} on the board`}</RowSub>
            </ListRow>
          ))}
        </Card>
      )}
      <span className="fsp-draft-entry-actions">
        {undo}
        {last === null ? (
          <Button variant="quiet" size="xs" disabled={busy} onClick={() => setReordering(true)}>
            Change the order
          </Button>
        ) : null}
      </span>
    </Stack>
  );
}

/**
 * An auction held in person, entered sale by sale (AUCTION.md §6.3): the
 * player, the team that bought him and the price. The team's most is shown as
 * it is chosen and checked by the server, so a slip of $85 for $8 is caught
 * where it is made.
 */
export function DraftSaleEntry({
  view,
  onView,
}: {
  view: DraftView;
  onView: (view: DraftView) => void;
}) {
  const leagueId = view.league.id;
  const auction = view.auction;
  const pool = valueOf(useDraftPool(leagueId).snapshot);
  const [query, setQuery] = useState('');
  const [player, setPlayer] = useState<DraftPoolPlayer | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const { busy, problem, step } = useStep(leagueId, onView);

  const taken = useMemo(
    () => new Set(view.board.flatMap((c) => (c.player ? [c.player.id] : []))),
    [view.board],
  );
  const matches = useMemo((): DraftPoolPlayer[] => {
    if (pool === null) return [];
    const words = plain(query).split(/\s+/).filter(Boolean);
    return pool.players
      .filter((p) => !taken.has(p.playerId))
      .filter((p) => words.every((w) => plain(p.name).includes(w)))
      .slice(0, SHOWN);
  }, [pool, query, taken]);

  if (auction === null) return null;
  if (reordering) {
    return <DraftOrderEntry view={view} onView={onView} onDone={() => setReordering(false)} />;
  }
  const money = new Map(auction.budgets.map((b) => [b.teamId, b]));
  const open = view.seats.filter((s) => (money.get(s.team.id)?.spotsLeft ?? 0) > 0);
  const sold = view.board.filter((c) => c.player !== null).length;
  const last = view.lastPick;
  const chosen = teamId === null ? null : (money.get(teamId) ?? null);
  const most = chosen?.most ?? 0;
  const ready =
    player !== null &&
    chosen !== null &&
    price !== null &&
    price >= auction.rules.minBid &&
    price <= most;

  const undo =
    last === null ? null : (
      <Button
        variant="secondary"
        size="xs"
        icon="undo"
        disabled={busy}
        onClick={() => void step({ action: 'undo', overall: last.overall })}
      >
        {`Take back the last sale${last.player ? `, ${last.player.shortName}` : ''}`}
      </Button>
    );

  if (open.length === 0) {
    return (
      <Stack gap={2}>
        <Callout tone="good">Every roster is full.</Callout>
        <Text size="sm" block>
          Finishing sets every team’s roster from these sales, starts each team’s lineup, puts a bot
          in any seat nobody has taken, and tells every manager. Nothing on this page takes it back.
        </Text>
        {problem === null ? null : <Callout tone="warn">{problem}</Callout>}
        <Button
          variant="primary"
          icon="accept"
          loading={busy}
          onClick={() => void step({ action: 'finish' })}
        >
          Finish the draft
        </Button>
        {undo}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Caption>{`Sale ${String(sold + 1)} of ${String(view.board.length)}`}</Caption>
      <TextField
        label="Find the player"
        placeholder="Type a name"
        icon="search"
        value={query}
        onChange={(q) => {
          setQuery(q);
          setPlayer(null);
        }}
        autoFocus
      />
      {player !== null ? (
        <ListRow lead={<PositionPill pos={player.positions[0] ?? '—'} size="sm" />} selected>
          <RowName>{player.name}</RowName>
          <RowSub>{player.proTeam === null ? 'Free agent' : clubLabel(player.proTeam)}</RowSub>
        </ListRow>
      ) : pool === null ? (
        <Text tone="secondary" size="sm">
          Loading the players…
        </Text>
      ) : matches.length === 0 ? (
        <Text tone="secondary" size="sm">
          Nobody left by that name.
        </Text>
      ) : (
        <Card>
          {matches.map((p) => (
            <ListRow
              key={p.playerId}
              onClick={() => setPlayer(p)}
              lead={<PositionPill pos={p.positions[0] ?? '—'} size="sm" />}
            >
              <RowName>{p.name}</RowName>
              <RowSub>{`${p.proTeam === null ? 'Free agent' : clubLabel(p.proTeam)} · #${String(p.rank)} on the board`}</RowSub>
            </ListRow>
          ))}
        </Card>
      )}
      <Choice
        label="Bought by"
        searchable={false}
        required
        value={teamId}
        onChange={setTeamId}
        options={open.map((s) => {
          const b = money.get(s.team.id)!;
          return {
            value: s.team.id,
            label: `${s.team.name} · $${String(b.left)} left · most $${String(b.most)}`,
          };
        })}
      />
      <NumberField
        label="Price"
        prefix="$"
        value={price}
        min={auction.rules.minBid}
        max={Math.max(auction.rules.minBid, most)}
        nullable
        onChange={setPrice}
        description={
          chosen === null
            ? 'Choose the team first.'
            : `From $${String(auction.rules.minBid)} to $${String(most)}: the most keeps $${String(auction.rules.minBid)} for each of its other spots.`
        }
      />
      {problem === null ? null : <Callout tone="warn">{problem}</Callout>}
      <Button
        variant="primary"
        icon="draft"
        loading={busy}
        disabled={!ready}
        onClick={() => {
          if (!ready) return;
          void step({
            action: 'sale',
            teamId: teamId!,
            playerId: player!.playerId,
            price: price!,
          }).then((done) => {
            if (!done) return;
            setQuery('');
            setPlayer(null);
            setPrice(null);
          });
        }}
      >
        {ready
          ? `${player!.name} to ${view.seats.find((s) => s.team.id === teamId)?.team.name ?? 'them'} for $${String(price)}`
          : 'Enter the sale'}
      </Button>
      <span className="fsp-draft-entry-actions">
        {undo}
        {last === null ? (
          <Button variant="quiet" size="xs" disabled={busy} onClick={() => setReordering(true)}>
            Change the order
          </Button>
        ) : null}
      </span>
    </Stack>
  );
}
